import { useEffect, useSyncExternalStore } from 'react'
import { fetchVersion } from '../api/client'
import { createStore } from '../lib/store'
import { CLIENT_BUILD } from './build'

/**
 * The client half of the skew handshake (design §5.3; architecture §8 — see
 * `packages/api/src/worker.ts`'s own top comment for the server half's full
 * design rationale, and `packages/api/src/floor.ts` for `belowFloor`'s own
 * parsing reasoning, neither repeated here).
 *
 * ONE module-scope store, written from TWO independent places, read from
 * ONE screen — that convergence is the point (task brief: "it must also
 * render when a command returns 426, so the two paths converge on one
 * screen"):
 *  1. `checkVersion` below — polled on boot and on window `focus` by
 *     `useSkewWatch`, comparing this build against the server's advertised
 *     `minClientBuild`. Catches the QUIET case: a device left open across a
 *     release, never issuing a command, that would otherwise only find out
 *     it's stale the moment it finally tries to write something.
 *  2. `markUpgradeRequired` — called by `api/client.ts`'s `runCommand` the
 *     instant a command comes back 426. Catches the case the poll missed
 *     (a release landing between one poll and the next) with certainty,
 *     because it is driven by the SAME enforcement the server actually
 *     applies, not a client-side guess about it.
 *
 * Neither path renders anything itself — both only flip this store, and
 * `AppShell` (the "shell" the brief's own wording names) is what actually
 * reads it and swaps its content for `UpgradeRequired`. That split is what
 * makes the convergence real rather than coincidental: there is exactly one
 * place in the whole app that decides whether this screen shows.
 */

/**
 * The store itself — `lib/store.ts`'s shared helper as of web-pre-deploy
 * item 9, no longer the hand-rolled `listeners`/`subscribe`/`getSnapshot`
 * this file used to spell out for itself. That copy was the ORIGINAL: both
 * `outbox/db.ts` and `outbox/clearance.ts` cite this file by name as their
 * precedent, which is exactly how a shape that was already missing a reset
 * propagated to three modules before anything corrected it.
 *
 * The equality guard this file used to state here moved WITH the shape
 * rather than being dropped: react-query, MSW and the boot/focus poll can
 * all race to set the SAME value in a real session (a second 426 after the
 * first already flipped it, a focus poll re-confirming a floor already
 * tripped), and `set` is a no-op for a value already held — see `Store.set`'s
 * own doc comment, and `test/store.test.ts`'s fourth case, which is what
 * keeps it from being deleted silently now that it lives somewhere shared.
 */
const upgradeRequiredStore = createStore(false)

/**
 * Exact-match SET membership — mirrors `packages/api/src/floor.ts`'s
 * `belowFloor` line for line (that file's own doc comment carries the full
 * reasoning, including the review-round-1 Critical/Important fix below):
 * build ids are content hashes (`tools/scripts/write-client-build.ts`) and
 * content hashes do not sort, so this can never be a `>=` compare, only "is
 * my build one of the ones the server still accepts".
 *
 * CRITICAL FIX (review round 1): the previous shape only treated `null`,
 * `undefined` and the exact empty string `''` as "no floor", so a
 * whitespace-only or comma-only `minClientBuild` (a stray space is the
 * realistic way an operator LIFTS an emergency floor on a platform where
 * vars are set rather than deleted) parsed to an empty entry that matched
 * no real build — refusing every client — while ALSO letting a
 * trailing-comma floor admit a client claiming `build: ''`. Fixed the same
 * way the server side is: filter empty entries out after splitting and
 * trimming, so an all-blank floor correctly reduces to "no floor" and no
 * build can ever match via an empty entry. `skew.test.ts` (this file's
 * sibling) pins the same six cases `floor.test.ts` does on the server side.
 *
 * EXPORTED (not private) so that sibling test can reach it directly.
 *
 * DUPLICATED rather than imported from the server: `packages/web` cannot
 * depend on `@gridfit/api` (a Workers-only package — hono, better-auth, D1
 * types — for three lines of pure string logic nothing else here needs), and
 * `tools/structural/web-imports.test.ts` polices the web bundle against
 * exactly that class of import. Kept in sync by inspection, not a shared
 * import — `skew.test.ts` and `floor.test.ts` run the IDENTICAL fixture
 * table against each copy independently, so a fix landing in only one is
 * caught by whichever file's own test still exercises the old, broken shape.
 */
export function isBelowFloor(build: string, floor: string | null | undefined): boolean {
  const entries = (floor ?? '')
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '')
  if (entries.length === 0) return false
  return !entries.includes(build)
}

async function checkVersion(): Promise<void> {
  try {
    const { minClientBuild } = await fetchVersion()
    if (isBelowFloor(CLIENT_BUILD, minClientBuild)) upgradeRequiredStore.set(true)
  } catch {
    // Best-effort: offline, or the API unreachable, is not evidence of skew
    // — it is evidence of a network problem, which the app's other offline
    // handling already owns. The 426 path above still enforces the floor
    // with certainty regardless of whether this poll ever completes.
    //
    // This `catch` now also absorbs a NON-OK status, which the previous
    // inline `fetch` handled with its own `if (!response.ok) return`:
    // `fetchVersion` (`api/client.ts`) throws on one, like every other read
    // there. Same observable outcome either way — no store write, no
    // rejection escaping — so the swallow this comment describes still
    // covers exactly the cases it always did.
  }
}

/**
 * Called by `api/client.ts`'s `runCommand` on a 426 response — the
 * authoritative half of this file's own top comment. Exported so that ONE
 * call site (that function) can flip the SAME store the boot/focus poll
 * writes to, without `client.ts` reaching into this module's private state
 * any other way.
 */
export function markUpgradeRequired(): void {
  upgradeRequiredStore.set(true)
}

/**
 * Test-only reset for this module's store — its value AND its subscribers.
 *
 * NAMED FOR WHAT IT DOES, web-pre-deploy item 10. The seven
 * `resetXForTests` exports in `core`/`contracts` (`resetRegistryForTests`,
 * `resetFoldsForTests`, `resetQueriesForTests`, `resetRefusalsForTests`,
 * `resetUpcastersForTests`, `resetCommandsForTests`, `resetShapesForTests`)
 * established that name for a plain, pure, one-way clear of a module's own
 * data. This one does something a reader carrying that assumption would not
 * expect: it also drops every live subscriber, so a component still mounted
 * across the call stops being notified. That is deliberate (see below), and
 * the name now says so rather than leaving it to be discovered.
 *
 * FINAL REVIEW, bucket 1 item 1 — this file shipped with NO reset at all,
 * the root cause (per the cross-cutting findings) `test/update-prompt.
 * test.tsx` worked around with THREE layered intra-file ordering
 * constraints instead, including one describe block documented "MUST run
 * LAST". REPRODUCED live before this fix: `pnpm vitest --root packages/web
 * run --sequence.shuffle.tests` failed at seeds 1, 42 and 2026 — every
 * failure in that one file, the stored flag latched `true` by an earlier
 * case and never reset, so a later case saw `AppShell`'s `UpgradeRequired`
 * screen instead of its own expected route. The subscribers go too, not just
 * the value — a stale subscriber left behind by an unmounted `useSkewWatch()`
 * instance (a test that renders, asserts, and never calls `stop()`) would
 * otherwise go on receiving notifications for a component that no longer
 * exists, in whichever test runs next. Both halves are now `createStore`'s
 * `reset()`, pinned by `test/store.test.ts`'s third case rather than by this
 * file re-deriving them.
 */
export function resetSkewAndSubscribersForTests(): void {
  upgradeRequiredStore.reset()
}

/**
 * Mounted from `AppShell` (design §5.3's "shell") — runs the boot check
 * immediately on mount and again on every `focus` (a background tab
 * regaining focus after a deploy is exactly when a stale build is most
 * likely to matter), and stays subscribed to the store for the rest of
 * `AppShell`'s lifetime so a 426 arriving mid-session (`markUpgradeRequired`
 * above) is picked up the same render pass a poll finding the same thing
 * would have been.
 */
export function useSkewWatch(): boolean {
  useEffect(() => {
    void checkVersion()
    const onFocus = (): void => void checkVersion()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [])

  return useSyncExternalStore(upgradeRequiredStore.subscribe, upgradeRequiredStore.getSnapshot)
}
